import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { getChangelogVersions, getChangelogContent, ChangelogVersion } from '@/lib/changelog';
import ReactMarkdown from 'react-markdown';
import { Loader2 } from 'lucide-react';

interface ChangelogModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ChangelogItem extends ChangelogVersion {
  content?: string;
  loading?: boolean;
}

export default function ChangelogModal({ open, onOpenChange }: ChangelogModalProps) {
  const [versions, setVersions] = useState<ChangelogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openVersions, setOpenVersions] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      loadVersions();
      setOpenVersions(new Set());
    }
  }, [open]);

  const handleValueChange = (openValues: string[]) => {
    setOpenVersions(new Set(openValues));
  };

  const loadVersions = async () => {
    setLoading(true);
    try {
      const changelogVersions = await getChangelogVersions();
      setVersions(
        changelogVersions.map((v) => ({
          ...v,
          content: undefined,
          loading: false,
        }))
      );
    } catch (error) {
      console.error('Error loading changelog versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChangelogContent = async (version: ChangelogItem) => {
    // If content is already loaded or loading, don't reload
    if (version.content !== undefined || version.loading) {
      return;
    }

    // Mark as loading
    setVersions((prev) =>
      prev.map((v) => (v.version === version.version ? { ...v, loading: true } : v))
    );

    try {
      const content = await getChangelogContent(version.filename);
      setVersions((prev) =>
        prev.map((v) =>
          v.version === version.version
            ? { ...v, content: content || 'No changelog content available.', loading: false }
            : v
        )
      );
    } catch (error) {
      console.error(`Error loading changelog for ${version.version}:`, error);
      setVersions((prev) =>
        prev.map((v) =>
          v.version === version.version
            ? { ...v, content: 'Error loading changelog content.', loading: false }
            : v
        )
      );
    }
  };

  // Load content when a version is opened
  useEffect(() => {
    openVersions.forEach((versionStr) => {
      const version = versions.find((v) => v.version === versionStr);
      if (version && version.content === undefined && !version.loading) {
        loadChangelogContent(version);
      }
    });
  }, [openVersions, versions]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Changelog</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading changelogs...</span>
          </div>
        ) : versions.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No changelogs available.
          </div>
        ) : (
          <Accordion type="multiple" className="w-full" onValueChange={handleValueChange}>
            {versions.map((version) => (
              <AccordionItem key={version.version} value={version.version}>
                <AccordionTrigger value={version.version}>
                  <span className="font-semibold">Version {version.version}</span>
                </AccordionTrigger>
                <AccordionContent value={version.version}>
                  {version.loading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      <span className="ml-2 text-xs text-muted-foreground">Loading...</span>
                    </div>
                  ) : version.content ? (
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                      <ReactMarkdown
                        components={{
                          h1: ({ children }) => (
                            <h1 className="text-xl font-bold mt-4 mb-2">{children}</h1>
                          ),
                          h2: ({ children }) => (
                            <h2 className="text-lg font-semibold mt-3 mb-2">{children}</h2>
                          ),
                          h3: ({ children }) => (
                            <h3 className="text-base font-semibold mt-2 mb-1">{children}</h3>
                          ),
                          p: ({ children }) => <p className="mb-2 text-sm">{children}</p>,
                          ul: ({ children }) => (
                            <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>
                          ),
                          ol: ({ children }) => (
                            <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>
                          ),
                          li: ({ children }) => <li className="text-sm">{children}</li>,
                          code: ({ children, className }) => {
                            const isInline = !className;
                            return isInline ? (
                              <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">
                                {children}
                              </code>
                            ) : (
                              <code className="block bg-muted p-2 rounded text-xs font-mono overflow-x-auto">
                                {children}
                              </code>
                            );
                          },
                          blockquote: ({ children }) => (
                            <blockquote className="border-l-4 border-muted-foreground pl-4 italic my-2">
                              {children}
                            </blockquote>
                          ),
                          a: ({ children, href }) => (
                            <a
                              href={href}
                              className="text-primary hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {children}
                            </a>
                          ),
                        }}
                      >
                        {version.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      No changelog content available for this version.
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </DialogContent>
    </Dialog>
  );
}
